// ⚠️ GENERATED COPY — DO NOT EDIT.
// Source: shared/ui/ParseoLogo.tsx — edit there, then run: bash scripts/sync_shared_code.sh
// CI verifies these copies match the source (sync_shared_code.sh --check).
import React from 'react';

/**
 * Parseo Health brand mark — "the mosaic": four rounded tiles in a 2x2 grid,
 * each a stakeholder in the PA workflow. Per the official Brand Guidelines:
 *   top-left  = teal  (#1B5E6E) — physician
 *   top-right = sage  (#6B9C8E) — patient
 *   bot-left  = amber (#E8943A) — pharmacy
 *   bot-right = navy  (#182A33) — insurer
 * Never rotate, skew, or rearrange the tile order. Scales crisply (SVG).
 *
 * Pass `animated` to turn it into a loading indicator: each tile pulses
 * (fades in/out) in a staggered clockwise ripple. Respects reduced-motion.
 * Pass `color` (e.g. "#ffffff") to render monochrome tiles on dark/colored
 * backgrounds so all four stay visible.
 */
const TILES = [
  { x: 6,  y: 6,  fill: '#1B5E6E', step: 0 }, // physician (top-left)
  { x: 34, y: 6,  fill: '#6B9C8E', step: 1 }, // patient   (top-right)
  { x: 6,  y: 34, fill: '#E8943A', step: 3 }, // pharmacy  (bottom-left)
  { x: 34, y: 34, fill: '#182A33', step: 2 }, // insurer   (bottom-right)
];

const STAGGER = 0.15; // seconds between each tile
const DUR = 1.2;      // seconds per pulse cycle

interface ParseoLogoProps {
  size?: number;
  animated?: boolean;
  color?: string;
  className?: string;
  style?: React.CSSProperties;
}

export default function ParseoLogo({ size = 40, animated = false, color, className = '', style }: ParseoLogoProps) {
  const reduced =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const pulse = animated && !reduced;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      className={className}
      style={style}
      role="img"
      aria-label={animated ? 'Loading' : 'Parseo Health'}
    >
      {TILES.map((t, i) => (
        <rect key={i} x={t.x} y={t.y} width="24" height="24" rx="6" fill={color || t.fill}>
          {pulse && (
            <animate
              attributeName="opacity"
              values="1;0.18;1"
              dur={`${DUR}s`}
              begin={`${t.step * STAGGER}s`}
              repeatCount="indefinite"
              calcMode="spline"
              keyTimes="0;0.5;1"
              keySplines="0.4 0 0.2 1;0.4 0 0.2 1"
            />
          )}
        </rect>
      ))}
    </svg>
  );
}

/**
 * Convenience loading indicator — the animated mosaic at a small size.
 * Pass `color="#ffffff"` when it sits on a dark/colored button so all four
 * tiles stay visible; omit it on light backgrounds for the brand mosaic.
 */
interface ParseoSpinnerProps {
  size?: number;
  color?: string;
  className?: string;
  style?: React.CSSProperties;
}

export function ParseoSpinner({ size = 24, color, className = '', style }: ParseoSpinnerProps) {
  return <ParseoLogo size={size} animated color={color} className={className} style={style} />;
}
